/**
 * Web-specific logger for Next.js app
 * Browser-safe with performance tracking
 */
import {
  type LogContext,
  createLogger,
  generateCorrelationId,
} from '@splitfiesta/logger';

// Create base logger
const baseLogger = createLogger('web');

// Store correlation ID in session storage
let currentCorrelationId: string | null = null;

function getOrCreateCorrelationId(): string {
  if (typeof window === 'undefined') {
    return generateCorrelationId();
  }

  if (!currentCorrelationId) {
    currentCorrelationId =
      sessionStorage.getItem('correlationId') || generateCorrelationId();
    sessionStorage.setItem('correlationId', currentCorrelationId);
  }

  return currentCorrelationId;
}

// Enhanced logger with web-specific features
export const logger = {
  // Expose base logger methods with proper binding
  debug: baseLogger.debug.bind(baseLogger),
  info: baseLogger.info.bind(baseLogger),
  warn: baseLogger.warn.bind(baseLogger),
  error: baseLogger.error.bind(baseLogger),
  time: baseLogger.time.bind(baseLogger),
  /**
   * Log API call with automatic timing
   */
  api(method: string, endpoint: string, context?: LogContext) {
    const correlationId = getOrCreateCorrelationId();
    const timer = baseLogger.time(`API ${method} ${endpoint}`, {
      ...context,
      correlationId,
      method,
      endpoint,
    });

    return {
      success: (data?: unknown) => {
        timer();
        baseLogger.info(`API ${method} ${endpoint} succeeded`, {
          ...context,
          correlationId,
          method,
          endpoint,
          responseSize: data ? JSON.stringify(data).length : 0,
        });
      },
      error: (error: Error, status?: number) => {
        timer();
        baseLogger.error(`API ${method} ${endpoint} failed`, error, {
          ...context,
          correlationId,
          method,
          endpoint,
          status,
        });
      },
    };
  },

  /**
   * Log user actions
   */
  action(action: string, context?: LogContext) {
    baseLogger.info(`User action: ${action}`, {
      ...context,
      correlationId: getOrCreateCorrelationId(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  },

  /**
   * Log navigation
   */
  navigation(from: string, to: string, context?: LogContext) {
    baseLogger.info('Navigation', {
      ...context,
      correlationId: getOrCreateCorrelationId(),
      from,
      to,
    });
  },

  /**
   * Log performance metrics
   */
  performance(metric: string, value: number, context?: LogContext) {
    baseLogger.info(`Performance: ${metric}`, {
      ...context,
      correlationId: getOrCreateCorrelationId(),
      metric,
      value,
      unit: 'ms',
    });
  },

  /**
   * Create page-specific logger
   */
  page(pageName: string) {
    return baseLogger.child({
      page: pageName,
      correlationId: getOrCreateCorrelationId(),
    });
  },

  /**
   * Get correlation ID for API calls
   */
  getCorrelationId(): string {
    return getOrCreateCorrelationId();
  },

  /**
   * Reset correlation ID (e.g., on logout)
   */
  resetCorrelationId() {
    currentCorrelationId = null;
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('correlationId');
    }
  },
};

// Make available in development
interface WindowWithLogger extends Window {
  logger?: typeof logger;
}

if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as WindowWithLogger).logger = logger;
}
